import React, { useState, useEffect } from 'react';
import { Button, notification, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import moment from 'moment';
import Table from '../../../Template/layoutComponents/Table';
import { getInstrumentCategories, deleteInstrumentCategory } from './services/instrumentCategory.service.ts';

const InstrumentCategoriesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { instance } = useMsal();
  const [categories, setCategories] = useState({ items: [], totalItems: 0 });
  const [notificationApi, contextHolder] = notification.useNotification();
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [deletingItem, setDeletingItem] = useState(null);

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return '-';

    const date = moment(dateString);
    if (!date.isValid()) return '-';

    return date.format('DD/MM/YYYY HH:mm');
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await getInstrumentCategories(instance);
      const transformedData = {
        items: data.items.map((cat) => ({
          ...cat,
          readingPropertiesCount: cat.readingProperties?.length || 0,
          propertiesCount: cat.properties?.length || 0,
        })),
        totalItems: data.totalItems,
        uniqueValues: data.uniqueValues || {},
      };
      setCategories(transformedData);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      notificationApi.error({
        message: 'Erro',
        description: 'Falha ao carregar categorias',
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (item) => {
    Modal.confirm({
      title: t('confirm_delete') || 'Confirmar exclusão',
      icon: <ExclamationCircleOutlined />,
      content: `${t('delete_confirmation') || 'Tem certeza que deseja excluir a categoria'} "${item.name}"?`,
      okText: t('delete') || 'Excluir',
      okType: 'danger',
      cancelText: t('cancel') || 'Cancelar',
      onOk: async () => {
        try {
          setDeletingItem(item.id);
          await deleteInstrumentCategory(instance, item.id);
          
          notificationApi.success({
            message: t('success') || 'Sucesso',
            description: t('category_deleted_successfully') || 'Categoria excluída com sucesso',
            duration: 3,
          });
          
          // Recarregar a lista
          loadCategories();
          setSelectedItems([]);
        } catch (error) {
          console.error('Erro ao excluir categoria:', error);
          notificationApi.error({
            message: t('error') || 'Erro',
            description: error?.response?.data?.message || t('delete_failed') || 'Falha ao excluir categoria',
            duration: 5,
          });
        } finally {
          setDeletingItem(null);
        }
      },
    });
  };

  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      background: '#fafafa' 
    }}>
      {contextHolder}

      {/* Header Fixo */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px',
          background: 'white',
          borderBottom: '1px solid #f0f0f0',
          flexShrink: 0
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
            {t('instrument_categories') || 'Categorias de Instrumentos'}
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>
            Gerencie as categorias de instrumentos do sistema
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          {selectedItems.length === 1 && (
            <Button
              danger
              size='large'
              icon={<DeleteOutlined />}
              loading={deletingItem === selectedItems[0].id}
              onClick={() => handleDelete(selectedItems[0])}
            >
              {t('delete') || 'Excluir'}
            </Button>
          )}
          <Button
            type='primary'
            size='large'
            icon={<PlusOutlined />}
            onClick={() => navigate('/settings/instrument-categories-v2/form')}
          >
            {t('new_category') || 'Nova Categoria'}
          </Button>
        </div>
      </div>

      {/* Área com Scroll */}
      <div style={{ 
        flex: 1, 
        overflow: 'auto',
        padding: '24px'
      }}>
        {/* Tabela */}
        <div style={{ background: 'white', borderRadius: '8px' }}>
        <Table
          handleRowClick={(row) => {
            navigate(`/settings/instrument-categories-v2/form/${row.id}`);
          }}
          handleSelect={(selected) => {
            setSelectedItems(selected);
          }}
          apiCall={(instance, params) => getInstrumentCategories(instance, params)}
          items={categories.items}
          startingTotalItems={categories.totalItems}
          tableSettings={{
            columnsConfig: {
              name: {
                label: t('name') || 'Nome',
              },
              createdAt: {
                label: t('created_at') || 'Criado em',
                replaceValue: (value) => ({
                  label: formatDate(value),
                  id: value || '',
                }),
              },
            },
          }}
        />
        </div>
      </div>
    </div>
  );
};

export default InstrumentCategoriesList;
